import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Upload, Mail, Phone, MapPin, Calendar, Building2, UserCheck, GraduationCap } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';

const GENRES = [
  { value: '', label: 'Sélectionner...' },
  { value: 'M.', label: 'Monsieur' },
  { value: 'Mme', label: 'Madame' }
];

const Profil = ({ user }) => {
  const [profile, setProfile] = useState({
    nom: user.nom || '',
    prenom: user.prenom || '',
    genre: '',
    email: user.email || '',
    telephone: user.telephone || '',
    date_naissance: '',
    lieu_de_naissance: '',
    adresse: '',
    code_postal: '',
    ville: '',
    groupe_sanguin: '',
    allergie: '',
    situation_handicap: '',
    autres_situations: '',
    contact_urgence_nom: '',
    contact_urgence_prenom: '',
    contact_urgence_filiation: '',
    contact_urgence_telephone: '',
    diplome: 'BPJEPS Activités du Motonautisme et Disciplines Associées',
    centre_formation: 'Atlantique',
    statut: 'stagiaire'
  });
  
  const [entreprise, setEntreprise] = useState(null);
  const [tuteur, setTuteur] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureRef = useRef(null);
  const [savedSignature, setSavedSignature] = useState(null);

  useEffect(() => {
    if (user.role === 'stagiaire') {
      loadProfile();
    }
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/stagiaires/${user.user_id}`);
      if (response.data) {
        setProfile({ ...profile, ...response.data });
        setSavedSignature(response.data.signature);
        
        // Charger entreprise et tuteur associés
        if (response.data.entreprise_id) {
          try {
            const entResp = await axios.get(`/entreprises/${response.data.entreprise_id}`);
            setEntreprise(entResp.data);
          } catch (e) { console.log('Entreprise non trouvée'); }
        }
        if (response.data.tuteur_id) {
          try {
            const tutResp = await axios.get(`/tuteurs/${response.data.tuteur_id}`);
            setTuteur(tutResp.data);
          } catch (e) { console.log('Tuteur non trouvé'); }
        }
      }
    } catch (error) {
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`/stagiaires/${user.user_id}`, {
        ...profile,
        user_id: user.user_id
      });
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    const signatureData = signatureRef.current.toDataURL();
    try {
      await axios.put(`/stagiaires/${user.user_id}`, {
        ...profile,
        signature: signatureData,
        user_id: user.user_id
      });
      setSavedSignature(signatureData);
      setShowSignaturePad(false);
      toast.success('Signature enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.put(`/stagiaires/${user.user_id}`, {
          ...profile,
          photo_identite: reader.result,
          user_id: user.user_id
        });
        setProfile({ ...profile, photo_identite: reader.result });
        toast.success('Photo mise à jour');
      } catch (error) {
        toast.error('Erreur lors de l\'upload');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="profil-page">
      {/* Header Bandeau Vert */}
      {user.role === 'stagiaire' && (
        <div className="bg-gradient-to-r from-[#8DC63F] to-[#6BA82A] rounded-xl p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Photo */}
            <div className="flex-shrink-0">
              <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden" data-testid="profile-photo-preview">
                {profile.photo_identite ? (
                  <img src={profile.photo_identite} alt="Photo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-12 w-12 text-white/60" />
                  </div>
                )}
              </div>
              <input 
                type="file" 
                id="photo-upload"
                accept="image/*" 
                onChange={handlePhotoUpload} 
                className="hidden" 
              />
              <label htmlFor="photo-upload" className="mt-2 block">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  className="w-full text-white hover:bg-white/20 text-xs"
                  onClick={() => document.getElementById('photo-upload').click()}
                >
                  <Upload className="h-3 w-3 mr-1" />
                  Photo
                </Button>
              </label>
            </div>
            
            {/* Infos principales */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-white/80 text-sm">{profile.genre || 'Genre'}</span>
                <h1 className="text-2xl md:text-3xl font-bold">
                  {profile.prenom} {profile.nom}
                </h1>
              </div>
              
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
                {profile.date_naissance && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Né(e) le {new Date(profile.date_naissance).toLocaleDateString('fr-FR')}
                    {profile.lieu_de_naissance && ` à ${profile.lieu_de_naissance}`}
                  </span>
                )}
              </div>
              
              <div className="bg-white/10 rounded-lg px-4 py-3">
                <p className="font-semibold flex items-center gap-2 mb-2">
                  <GraduationCap className="h-5 w-5" />
                  {profile.diplome || 'BPJEPS Activités du Motonautisme et Disciplines Associées'}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    Centre {profile.centre_formation || 'Atlantique'}
                  </span>
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                    {profile.statut === 'apprenti' ? 'Apprenti' : 'Stagiaire'}
                  </span>
                  {entreprise && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      Entreprise: {entreprise.profile?.nom || entreprise.nom || 'N/A'}
                    </span>
                  )}
                  {tuteur && (
                    <span className="flex items-center gap-1">
                      <UserCheck className="h-4 w-4" />
                      Tuteur: {tuteur.profile?.prenom || tuteur.prenom} {tuteur.profile?.nom || tuteur.nom}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informations Personnelles */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Informations Personnelles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Genre */}
          <div className="space-y-2">
            <Label htmlFor="genre">Genre *</Label>
            <select
              id="genre"
              value={profile.genre || ''}
              onChange={(e) => setProfile({ ...profile, genre: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-genre"
            >
              {GENRES.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          
          {/* Prénom */}
          <div className="space-y-2">
            <Label htmlFor="prenom">Prénom *</Label>
            <Input 
              id="prenom" 
              value={profile.prenom} 
              onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} 
              data-testid="input-prenom" 
            />
          </div>
          
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="nom">Nom *</Label>
            <Input 
              id="nom" 
              value={profile.nom} 
              onChange={(e) => setProfile({ ...profile, nom: e.target.value })} 
              data-testid="input-nom" 
            />
          </div>

          {user.role === 'stagiaire' && (
            <>
              {/* Date de naissance */}
              <div className="space-y-2">
                <Label htmlFor="date_naissance">Date de Naissance *</Label>
                <Input 
                  id="date_naissance" 
                  type="date" 
                  value={profile.date_naissance} 
                  onChange={(e) => setProfile({ ...profile, date_naissance: e.target.value })} 
                  data-testid="input-date-naissance" 
                />
              </div>
              
              {/* Lieu de naissance */}
              <div className="space-y-2">
                <Label htmlFor="lieu_de_naissance">Lieu de Naissance *</Label>
                <Input 
                  id="lieu_de_naissance" 
                  value={profile.lieu_de_naissance || ''} 
                  onChange={(e) => setProfile({ ...profile, lieu_de_naissance: e.target.value })} 
                  placeholder="Ville de naissance" 
                  data-testid="input-lieu-naissance" 
                />
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    id="email" 
                    type="email"
                    value={profile.email || ''} 
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })} 
                    className="pl-10"
                    placeholder="email@exemple.fr"
                    data-testid="input-email" 
                  />
                </div>
              </div>
              
              {/* Téléphone */}
              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    id="telephone" 
                    value={profile.telephone} 
                    onChange={(e) => setProfile({ ...profile, telephone: e.target.value })} 
                    className="pl-10"
                    placeholder="06 00 00 00 00"
                    data-testid="input-telephone" 
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Adresse (séparée en 3 champs) */}
      {user.role === 'stagiaire' && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#8DC63F]" />
            Adresse
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-3">
              <Label htmlFor="adresse">Adresse (numéro et rue)</Label>
              <Input 
                id="adresse" 
                value={profile.adresse} 
                onChange={(e) => setProfile({ ...profile, adresse: e.target.value })} 
                placeholder="123 rue de la Formation"
                data-testid="input-adresse" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code_postal">Code Postal</Label>
              <Input 
                id="code_postal" 
                value={profile.code_postal || ''} 
                onChange={(e) => setProfile({ ...profile, code_postal: e.target.value })} 
                placeholder="17110"
                maxLength={5}
                data-testid="input-code-postal" 
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="ville">Ville</Label>
              <Input 
                id="ville" 
                value={profile.ville || ''} 
                onChange={(e) => setProfile({ ...profile, ville: e.target.value })} 
                placeholder="Saint-Georges-de-Didonne"
                data-testid="input-ville" 
              />
            </div>
          </div>
        </div>
      )}

      {/* Informations Médicales */}
      {user.role === 'stagiaire' && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Informations Médicales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="groupe_sanguin">Groupe Sanguin</Label>
              <select
                id="groupe_sanguin"
                value={profile.groupe_sanguin || ''}
                onChange={(e) => setProfile({ ...profile, groupe_sanguin: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
                data-testid="select-groupe-sanguin"
              >
                <option value="">Sélectionner...</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="allergie">Allergies</Label>
              <Input 
                id="allergie" 
                value={profile.allergie} 
                onChange={(e) => setProfile({ ...profile, allergie: e.target.value })} 
                placeholder="Aucune ou préciser"
                data-testid="input-allergie" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="situation_handicap">Situation de Handicap</Label>
              <Input 
                id="situation_handicap" 
                value={profile.situation_handicap} 
                onChange={(e) => setProfile({ ...profile, situation_handicap: e.target.value })} 
                placeholder="Aucune ou préciser"
                data-testid="input-handicap" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="autres_situations">Autres Situations Particulières</Label>
              <Input 
                id="autres_situations" 
                value={profile.autres_situations} 
                onChange={(e) => setProfile({ ...profile, autres_situations: e.target.value })} 
                data-testid="input-autres" 
              />
            </div>
          </div>
        </div>
      )}

      {/* Contact d'Urgence */}
      {user.role === 'stagiaire' && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Contact d'Urgence</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_nom">Nom</Label>
              <Input 
                id="contact_nom" 
                value={profile.contact_urgence_nom} 
                onChange={(e) => setProfile({ ...profile, contact_urgence_nom: e.target.value })} 
                data-testid="input-contact-nom" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_prenom">Prénom</Label>
              <Input 
                id="contact_prenom" 
                value={profile.contact_urgence_prenom} 
                onChange={(e) => setProfile({ ...profile, contact_urgence_prenom: e.target.value })} 
                data-testid="input-contact-prenom" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_filiation">Lien de parenté</Label>
              <select
                id="contact_filiation"
                value={profile.contact_urgence_filiation || ''}
                onChange={(e) => setProfile({ ...profile, contact_urgence_filiation: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
                data-testid="select-contact-filiation"
              >
                <option value="">Sélectionner...</option>
                <option value="Père">Père</option>
                <option value="Mère">Mère</option>
                <option value="Conjoint(e)">Conjoint(e)</option>
                <option value="Frère">Frère</option>
                <option value="Soeur">Soeur</option>
                <option value="Ami(e)">Ami(e)</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_telephone">Téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  id="contact_telephone" 
                  value={profile.contact_urgence_telephone} 
                  onChange={(e) => setProfile({ ...profile, contact_urgence_telephone: e.target.value })} 
                  className="pl-10"
                  data-testid="input-contact-telephone" 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature */}
      {(user.role === 'stagiaire' || user.role === 'tuteur' || user.role === 'formateur') && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Signature</h2>
          {!showSignaturePad && savedSignature ? (
            <div>
              <div className="border border-slate-200 rounded-lg p-4 inline-block bg-slate-50" data-testid="saved-signature">
                <img src={savedSignature} alt="Signature" className="h-24" />
              </div>
              <div className="mt-4">
                <Button onClick={() => setShowSignaturePad(true)} variant="outline" data-testid="change-signature-button">
                  Modifier la signature
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500 mb-2">Dessinez votre signature dans le cadre ci-dessous</p>
              <div className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid="signature-canvas">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-48 bg-white'
                  }}
                />
              </div>
              <div className="flex space-x-3 mt-4">
                <Button onClick={handleSaveSignature} className="bg-[#8DC63F] hover:bg-[#7db336]" data-testid="save-signature-button">
                  Valider
                </Button>
                <Button onClick={clearSignature} variant="outline" data-testid="clear-signature-button">
                  Effacer
                </Button>
                {savedSignature && (
                  <Button onClick={() => setShowSignaturePad(false)} variant="outline" data-testid="cancel-signature-button">
                    Annuler
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-[#8DC63F] hover:bg-[#7db336] text-white px-8" data-testid="save-profile-button">
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
};

export default Profil;
